import { BOARD_TILE_ABILITIES } from './doorBoard';
import './BoardConditions.css';
import CoreboundIcon from './CoreboundIcon';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { usePopupTransition } from './popupTransition';

const TILE_LEGEND_DESCRIPTIONS = {
  relay: 'Choose a neighboring tile and use its effect instead.',
  gambit: 'Spend 1 shard to reroll and launch the die onto a new tile.',
  powerUp:
    'Relaunch with +20% power per tile level, up to +200% at ×10. Each activation raises its level.',
  interference: 'Reduce the Core’s next attack by 1 HP damage.',
  sabotage:
    'On the next Core throw, double-damage tiles become misses and positive bonuses become penalties.',
  recharge: 'Restore a spent Phase Anchor. Chain Reaction cannot recharge.',
};

export const closeDisclosureOnEscape = (event) => {
  if (event.key !== 'Escape' || !event.currentTarget.open) return;
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.open = false;
  event.currentTarget.querySelector('summary')?.focus();
};

export const closeOtherInlineDisclosure = (event) => {
  if (!event.currentTarget.open) return;
  event.currentTarget
    .closest('.coreboundConditionControls')
    ?.querySelectorAll(
      '.coreboundConditionInline[open], .coreboundBoardTileLegend[open]'
    )
    .forEach((disclosure) => {
      if (disclosure !== event.currentTarget) disclosure.open = false;
    });
};

const TileLegendPopup = ({ onClose, returnFocusRef }) => {
  const { close, popupClass } = usePopupTransition(onClose);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    const unlock = lockBodyScroll();
    const trigger = returnFocusRef.current;
    const dialog = dialogRef.current;
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      unlock();
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);
  return createPortal(
    <dialog
      ref={dialogRef}
      className={`coreboundBoardTileLegendPanel ${popupClass}`}
      aria-modal="true"
      aria-label="Board tile legend"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          closeRef.current?.focus();
        }
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          close();
      }}
    >
      <header>
        <h2 className="coreboundBoardTileLegendTitle">Tile legend</h2>
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label="Close board tile legend"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M4 4l12 12M16 4L4 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>
      <span className="coreboundBoardTileLegendIntro">
        Special tiles give you extra choices when your die lands.
      </span>
      <ul>
        {Object.entries(BOARD_TILE_ABILITIES).map(([type, ability]) => (
          <li key={type} style={{ '--tile-legend-color': ability.color }}>
            <span className="coreboundBoardTileLegendIcon" aria-hidden="true">
              <CoreboundIcon name={type} />
            </span>
            <span className="coreboundBoardTileLegendCopy">
              <strong>{ability.name}</strong>
              {ability.coreOnly && <small>CORE ONLY</small>}
              <span>
                {TILE_LEGEND_DESCRIPTIONS[type] || ability.description}
              </span>
            </span>
          </li>
        ))}
        <li style={{ '--tile-legend-color': '#ca79ff' }}>
          <span
            className="coreboundBoardTileLegendIcon is-corrupted"
            aria-hidden="true"
          >
            −100
          </span>
          <span className="coreboundBoardTileLegendCopy">
            <strong>Corrupted</strong>
            <small>CORE ONLY</small>
            <span>
              Replaces beneficial tiles with a −100 roll penalty for this fight.
              Aim around them.
            </span>
          </span>
        </li>
      </ul>
    </dialog>,
    document.body
  );
};

const BoardTileLegend = ({ onOpen }) => {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  return (
    <span className="coreboundBoardTileLegend">
      <button
        ref={trigger}
        type="button"
        aria-label="Open board tile legend"
        title="Tile legend"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          onOpen?.();
          setOpen(true);
        }}
      >
        i
      </button>
      {open && (
        <TileLegendPopup
          onClose={() => setOpen(false)}
          returnFocusRef={trigger}
        />
      )}
    </span>
  );
};

export default BoardTileLegend;
